// 카피바라 한 마리의 상태 (내 카피바라와 NPC 공용). 프로토타입 makeAgent 1617–1631.
// 방·음식·소품 모듈이 쓰는 필드는 선택(?)으로 두고, 해당 모듈이 옮겨질 때 채운다.
import * as THREE from 'three';
import { FUR, makeCapy, matsFor, type CapyModel, type FurTone } from './model';
import type { NavPath } from '../engine/nav';
import type { HandItemId, HeadItemId } from '@capy/shared';
import type { FaceKey } from './equip';
import type { Scratcher } from '../items/hand';
import type { Carry, EatFx, Eating } from '../food/eating';
import type { Dish } from '../food/dishes';
import type { Hop } from './stacking';
import type { RollAnim } from './lie';

export type EmoteKey = 'wave' | 'wobble' | 'waddle' | 'yawn';

export interface Capy {
  isPlayer: boolean;
  c: CapyModel;
  /** 물에 들어갔을 때 몸 주위에 퍼지는 링. */
  ripple: THREE.Mesh;
  pos: THREE.Vector3;
  facing: number;
  targetFacing: number;
  size: number;
  tone: FurTone;

  // 걷기 (walker.ts)
  goal: THREE.Vector3 | null;
  path: NavPath | null;
  pathI: number;
  pathGX: number;
  pathGZ: number;
  repathAt: number;
  lastDg: number | null;
  lastProg: number;

  // 자세 (pose.ts)
  walkAmt: number;
  walkPhase: number;
  breath: number;
  seedP: number;
  swimK: number;
  napping: boolean;
  napAmt: number;
  emote: EmoteKey | null;
  emoteStart: number;
  /** 0 = 땅, 1 = 물에 잠김. */
  bathAmt: number;
  rippleT: number;
  /** 소품이 머리 위로 떨어지며 튕기기 시작한 시각 (-1이면 없음). */
  dropStart: number;
  chew?: number;
  chewUntil?: number;
  benchAmt?: number;
  seatLiftV?: number;
  lieAmt?: number;
  /** 눕기 (찜질방). */
  lying?: boolean;
  /** 누운 채 옆으로 구른 각도. */
  roll?: number;
  rollAnim?: RollAnim | null;
  /** 앞으로 한 바퀴 도는 비율 (찜질방 놀이터). */
  flipK?: number;
  bounceY?: number;
  /** 2층 마루 높이. */
  elevY?: number;
  washT?: number;
  scrubT?: number;
  scratchT?: number;
  heat?: number;
  cold?: number;
  spicyUntil?: number;

  // 방이 채우는 값 (4단계)
  mode?: string;
  seated?: boolean;
  spot?: { pose?: string; seatY?: number } | null;
  spotPending?: boolean;
  tray?: unknown;

  // 소품 (equip.ts)
  item: THREE.Object3D | null;
  itemKey?: HeadItemId | 'none';
  /** 지금 얼굴에 있는 것 (라군 팩 포함). */
  faceKey?: FaceKey;
  /** 내가 고른 얼굴 소품 (라군 팩을 씻어 내면 이걸로 돌아감). */
  wornFace?: FaceKey;
  mask?: THREE.Mesh | null;
  maskPop?: number;
  cukes?: THREE.Object3D | null;
  bubble?: THREE.Object3D | null;
  bubbleK?: number;
  handKey?: HandItemId | 'none';
  /** 손에 붙은 장갑 또는 효자손 그룹. */
  mitt?: THREE.Object3D | null;
  scratcher?: Scratcher | null;

  // 음식 (food/eating.ts)
  eating?: Eating | null;
  fx?: EatFx | null;
  carry?: Carry | null;
  dish?: Dish | null;
  sticks?: THREE.Group | null;
  noodles?: THREE.Group | null;

  // 올라타기 (stacking.ts, 판정은 stackRules.ts)
  /** 내 위에 다른 카피바라가 올라와도 되는지. */
  allowRide: boolean;
  /** 실내 식당 손님처럼 지금 올라타기 대상이 될 수 없음. */
  unavailable?: boolean;
  riding?: Capy | null;
  rider?: Capy | null;
  hop?: Hop | null;
  climbT?: Capy | null;
  climbGoal?: THREE.Vector3;
  climbRetarget?: number;
  afterHop?: ((a: Capy) => void) | null;
}

const rippleGeo = new THREE.RingGeometry(0.9, 0.96, 40);

/** own이면 머티리얼을 공유하지 않는다 (내 카피바라: 털 색을 바꿀 수 있게). */
export function makeAgent(parent: THREE.Object3D, isPlayer: boolean, tone: FurTone): Capy {
  const c = makeCapy(matsFor(tone, isPlayer));
  parent.add(c.root);
  const ripple = new THREE.Mesh(rippleGeo, new THREE.MeshBasicMaterial({ color: 0xFFFFFF, transparent: true, opacity: 0, depthWrite: false }));
  ripple.rotation.x = -Math.PI / 2;
  ripple.visible = false;
  parent.add(ripple);
  const a: Capy = {
    isPlayer, c, ripple, pos: new THREE.Vector3(), facing: 0, targetFacing: 0, size: 1, tone,
    goal: null, path: null, pathI: 0, pathGX: NaN, pathGZ: NaN, repathAt: 1e9, lastDg: null, lastProg: 0,
    walkAmt: 0, walkPhase: Math.random() * 6, breath: Math.random() * 6, seedP: Math.random() * 10, swimK: 0,
    napping: false, napAmt: 0, emote: null, emoteStart: 0, bathAmt: 0, rippleT: Math.random() * 3, dropStart: -1,
    item: null, allowRide: isPlayer || Math.random() < 0.85,
  };
  c.body.userData.agent = a;
  c.headG.children[0]!.userData.agent = a;
  return a;
}

/** 1 = 어른, 0.5 = 아기 (머리가 조금 더 크다). */
export function setSize(a: Capy, s: number): void {
  a.size = s;
  a.c.root.scale.setScalar(s);
  a.c.headPivot.scale.setScalar(s < 1 ? 1.22 : 1);
}

/** 털 색 바꾸기. 프로토타입과 같이 몸 색·테두리 빛과 짙은 부분 색만 바꾼다 (own 머티리얼일 때만). */
export function setFur(a: Capy, tone: FurTone): void {
  const f = FUR[tone], m = a.c.body.material as THREE.MeshStandardMaterial;
  m.color.set(f.fur);
  (m.userData.rim as { value: THREE.Color }).value.set(f.rim);
  (a.c.feet[0]!.material as THREE.MeshStandardMaterial).color.set(f.dark);
  a.tone = tone;
}
